<template>
  <div class="table-list-container">
    <div class="searchbar-container mb-1">
      <span
        class="searchbar-icon"
        v-html="searchIcon"
      />
      <input
        v-model.lazy="searchTerm"
        class="searchbar"
        :placeholder="'Search ' + label + 's...'"
      />
      <span
        v-show="searchTerm"
        id="clear-search"
        v-tooltip="{ content: 'Clear Search', placement: 'right' }"
        class="searchbar-clear"
        @click="clearSearch"
        v-html="clearIcon"
      />
    </div>
    <template v-if="listItems.length > 0">
      <div class="table-list-topbar">
        <div class="table-list-topbar__selected">
          {{ totalSelected }} {{ label }}s Selected
          <transition name="fade">
            <button
              v-show="totalSelected > 0"
              id="clear-button"
              class="btn btn--grey table-list-topbar__btn"
              @click="clearSelectedItems"
            >
              Clear
            </button>
          </transition>
        </div>
        <button
          id="select-all-button"
          class="btn btn--grey table-list-topbar__btn"
          @click="selectAllItems"
        >
          Select All
        </button>
      </div>
      <table class="table-list">
        <div class="table-list__item table-list__header">
          <span />
          <template v-for="(header, index) in tableHeaders">
            <span :key="index">
              {{ header.label }}
            </span>
          </template>
        </div>
        <div
          v-for="(item, index) in listItems"
          :id="label.toLowerCase() + '-item-' + index"
          :key="index"
          :data-testid="item[testLabel]"
          :class="[{ 'table-list__item--selected': isSelected(item) }, 'table-list__item']"
          @click="selectItem(item, index)"
        >
          <input
            type="checkbox"
            :checked="isSelected(item)"
            class="checkbox"
          />
          <span
            v-for="(header, headerIndex) in tableHeaders"
            :key="headerIndex"
            :class="header.class"
          >{{ item[header.prop] }}</span>
        </div>
      </table>
      <pagination
        :page.sync="paginationParams.page"
        :per-page.sync="paginationParams.perPage"
        :total-rows.sync="totalRows"
        :rows-per-page-label="`${label}s per page`"
      />
    </template>
    <template v-else>
      <slot name="empty">
        <div class="empty-state">
          Could not find any items.
        </div>
      </slot>
    </template>
  </div>
</template>

<script>
import Pagination from '@/components/pagination.vue'
import feather from 'feather-icons'

export default {
  name: 'SelectFromList',
  components: {
    Pagination,
  },
  props: [
    'listItems',
    'totalRows',
    'paginationParams',
    'tableHeaders',
    'label',
    'selectedItems',
    'search',
    'testLabel',
  ],
  data() {
    return {
      searchIcon: feather.icons.search.toSvg(),
      clearIcon: feather.icons.x.toSvg({width: '1em'}),
    }
  },
  computed: {
    searchTerm: {
      get() {
        return this.search
      },
      set(value) {
        this.$emit('update:search', value)
      },
    },
    totalSelected() {
      if (this.selectedItems) {
        return this.selectedItems.length
      }
      return 0
    },
    isAllSelected() {
      return this.listItems.every((item) => {
        return this.selectedItems.includes(item)
      })
    },
  },
  methods: {
    clearSearch() {
      this.searchTerm = ''
    },
    isSelected(checkItem) {
      if (this.selectedItems) {
        return this.selectedItems.some((item) => {
          return Object.values(item)[0] === Object.values(checkItem)[0]
        })
      }
    },
    /*
     * Can't use indexOf here because when switching between pages the user
     * object seems to be different. Need to search for matching username
     * instead to remove from selected list.
     */
    selectItem(selectedItem) {
      if (this.isSelected(selectedItem)) {
        this.removeSelectedItem(selectedItem)
      } else {
        this.addSelectedItem(selectedItem)
      }
    },
    addSelectedItem(selectedItem) {
      if (selectedItem.callback && !selectedItem.callback(selectedItem)) {
        return
      }
      this.selectedItems.push(selectedItem)
      selectedItem.selected = true
    },
    removeSelectedItem(selectedItem) {
      const _selectedItemIndex = this.selectedItems.findIndex((item) => {
        return Object.values(item)[0] === Object.values(selectedItem)[0]
      })
      this.selectedItems.splice(_selectedItemIndex, 1)
      selectedItem.selected = false
    },
    clearSelectedItems() {
      this.selectedItems.splice(0, this.selectedItems.length)
    },
    selectAllItems() {
      if (this.isAllSelected) {
        this.clearSelectedItems()
      } else {
        this.listItems.forEach((item) => {
          if (!this.isSelected(item)) {
            this.selectedItems.push(item)
          }
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_tables.scss";

.table-list {
  width: 100%;
  &__item {
    @include grid-table-item();
    grid-template-columns: 0.15fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 1em;
    padding: 0.5em;
    & .right {
      @include flex-display();
      @include flex-justify-content(center);
      @include flex-align-items(center);
      width: 100%;
      &--inactive {
        font-size: $tiny-font;
        color: $grey-text;
        background: $off-white;
        font-weight: bold;
        text-transform: uppercase;
        padding: 5px;
        border-radius: 5px;
        width: fit-content;
        height: fit-content;
        &:empty {
          display: none;
        }
      }
    }
    &--selected {
      background-color: $light-blue;
    }
    &:hover {
      background-color: lighten($light-blue, 7%);
    }
  }
  &__header {
    @include grid-table-header();
    font-size: $small-font;
  }
  &-topbar {
    @include flex-display();
    @include flex-justify-content(space-between);
    @include flex-align-items(center);
    margin-bottom: 1em;
    font-size: $small-font;
    font-style: italic;
    &__btn {
      margin-left: 1em;
      font-size: 0.7em;
    }
    &__selected {
      @include flex-display();
      @include flex-align-items(center);
    }
  }
  &-container {
    padding: 1em;
    border: 1px solid $light-grey;
    border-radius: 5px;
  }
}
</style>
